import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { uid } from 'quasar'
import {
  tabOutlineMap,
  tabOutlineMapMinus,
  tabOutlineCube,
  tabOutlineCubeOff
} from 'quasar-extras-svg-icons/tabler-icons-v3'
import { i18n } from 'src/utils/i18n-instance'

import type { BasicDigitalTwin } from 'models/BasicDigitalTwin'
import type { BasicRelationship } from 'models/BasicRelationship'
import type { ViewerTwin } from 'models/ViewerTwin'
import type { ForgeHubModelTwin, ViewerDataTwin } from 'models/ViewerDataTwin'
import type AzureMapsViewer from 'components/viewers/AzureMapsViewer'
import type ForgeViewer from 'components/viewers/ForgeViewer'
import type {
  AddMarkerFunction,
  GetObjectPropertiesFunction,
  ViewerObjectPropertyResult,
  ViewerProperty
} from 'components/viewers'
import type { MapTwin } from 'models/ViewerDataTwin'
import {
  ALLOWED_FORGE_MODEL_IDS,
  ALLOWED_MAP_MODEL_IDS,
  REL_TYPE_OUTGOING,
  VIEWER_DATA_REL_NAME
} from 'services/constants'
import { useModelsStore } from './models'
import { useDigitalTwinsStore } from './digital-twins'
import { useSelectedStore } from './selected'
import { useViewerDataStore } from './viewerData'
import { tryJexlEval } from 'utils/jexl'
import { isTwinIdExpression, evaluateTwinIdExpression } from 'utils/twin-id-expression'

export const enum ViewerType {
  AzureMapsViewer,
  ForgeViewer,
  CytoscapeGraphViewer
}

/** Visibility snapshot taken when a load starts, used to tell a stale hide from an in-flight one. */
type VisibleSnapshot = Record<string, boolean>

/**
 * Mark a viewer-data twin visible in the viewerData store once it is loaded.
 *
 * A `false` that already existed when the load started is stale (eg. the panel was toggled off
 * before the layer was loaded again): the layer is now on the map, so the panel must show it on.
 * A `false` that appears while the load is in flight is an explicit user hide and must win over
 * the load completing — the viewer container's toggle-off reconciliation depends on it.
 */
function markViewableVisible(twin: ViewerDataTwin, visibleAtLoadStart: VisibleSnapshot) {
  const viewerDataStore = useViewerDataStore()
  const hiddenDuringLoad =
    viewerDataStore.visible[twin.$dtId] === false && visibleAtLoadStart[twin.$dtId] !== false
  if (hiddenDuringLoad) return
  viewerDataStore.toggleVisibility(twin, true)
}

/** '='-prefixed twinIdProperty expressions from the loaded viewer-data twins. */
function collectExpressionTwinIdProperties(viewerDataTwins: ViewerDataTwin[]): string[] {
  return [...new Set(viewerDataTwins.map((vd) => vd.twinIdProperty).filter(isTwinIdExpression))]
}

/** Expand one non-expression twinIdProperty (plain name or JEXL name list) into names. */
function expandNameTwinIdProperty(twinIdProperty: string | undefined): string[] {
  const evaluated = tryJexlEval<string | string[]>(twinIdProperty)
  const names = Array.isArray(evaluated) ? evaluated : [evaluated]
  return names.filter((p): p is string => typeof p === 'string' && p.length > 0)
}

/** Legacy name-based twinIdProperty names (excludes '=' expressions). */
function collectNameTwinIdProperties(viewerDataTwins: ViewerDataTwin[]): string[] {
  return [
    ...new Set(
      viewerDataTwins
        .filter((vd) => !isTwinIdExpression(vd.twinIdProperty))
        .flatMap((vd) => expandNameTwinIdProperty(vd.twinIdProperty))
    )
  ]
}

/** Find the selected object whose name-based twin-id property matches twinId. */
function findByNameTwinId(
  objects: ViewerObjectPropertyResult[],
  twinIdProperties: string[],
  twinId: string
): ViewerObjectPropertyResult | undefined {
  if (twinIdProperties.length === 0) return undefined
  return objects.find(
    (vo) =>
      vo.properties.find((p) => twinIdProperties.includes(p.attributeName))?.displayValue === twinId
  )
}

/** Whether a viewer property carries a usable display value for expression matching. */
function isMatchableProperty(p: ViewerProperty): boolean {
  return (
    !!p.displayName &&
    p.displayValue !== undefined &&
    p.displayValue !== null &&
    String(p.displayValue).length > 0
  )
}

/** Index an object's display properties by name (first value wins). */
function toDisplayProperties(vo: ViewerObjectPropertyResult): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  for (const p of vo.properties) {
    if (p.displayName && isMatchableProperty(p) && !(p.displayName in properties)) {
      properties[p.displayName] = p.displayValue
    }
  }
  return properties
}

/** Find the selected object whose '=' expression twin-id evaluates to twinId. */
function findByExpressionTwinId(
  objects: ViewerObjectPropertyResult[],
  expressionTwinIdProperties: string[],
  twinId: string
): ViewerObjectPropertyResult | undefined {
  if (expressionTwinIdProperties.length === 0) return undefined
  for (const vo of objects) {
    const properties = toDisplayProperties(vo)
    if (
      expressionTwinIdProperties.some(
        (expr) => evaluateTwinIdExpression(expr, properties) === twinId
      )
    ) {
      return vo
    }
  }
  return undefined
}

/** The store used to intereact between features and the main viewer container */
export const useViewersStore = defineStore('viewers', () => {
  /** Currently loaded main viewer */
  const currentViewer = ref(ViewerType.AzureMapsViewer)
  /** Will be linked to the Azure Maps Viewer Ref in the ViewerContainer template */
  const azureMapsViewer = ref<InstanceType<typeof AzureMapsViewer> | undefined>(undefined)
  /** Will be linked to the Forge Viewer Ref in the ViewerContainer template */
  const forgeViewer = ref<InstanceType<typeof ForgeViewer> | undefined>(undefined)
  /** Currently loaded viewer data twins */
  const viewerDataTwins = ref<ViewerDataTwin[]>([])
  /** Suggested viewer data */
  const suggestedViewerDataTwin = ref<(ViewerDataTwin & { $suggestName?: string }) | undefined>(
    undefined
  )
  /** Currently loaded viewer twins */
  const twins = ref<ViewerTwin[]>([])
  /** Currently loaded viewer relationships (for future use in CytoScape) */
  const relationships = ref<BasicRelationship[]>([])
  /** Center of map */
  const center = ref<[longitude: number, latitude: number] | undefined>(undefined)
  /** Stringified state to be loaded (only used for Forge Viewer) */
  const state = ref<{ [key: string]: string }>({})
  /** Whether to isolate the provided twins */
  const isolate = ref<boolean | undefined>(false)
  /** Holds the amount of currently selected objects in the viewer (used to decide whether selected objects can be linked/imported) */
  const selectedCount = ref(0)

  /** Returns the current viewer component instance */
  const currentViewerComponent = computed(() => {
    // NOTE: There's no need for this for the graph viewer as it doesn't have this functionality
    // TODO: check if viewer is actually visible
    if (currentViewer.value === ViewerType.AzureMapsViewer && azureMapsViewer.value) {
      return azureMapsViewer.value
    } else if (currentViewer.value === ViewerType.ForgeViewer && forgeViewer.value) {
      return forgeViewer.value
    } else {
      return undefined
    }
  })

  /** All Forge viewables in the environment */
  const allForgeViewables = computed(() => {
    const twinsStore = useDigitalTwinsStore()
    const { twinIsOfModel } = useModelsStore()
    return twinsStore.allTwins.filter((v): v is ForgeHubModelTwin =>
      twinIsOfModel<ForgeHubModelTwin>(v, ALLOWED_FORGE_MODEL_IDS)
    )
  })

  /** Forge viewables (filtered from viewer data twins list) */
  const forgeViewables = computed(() => {
    const { twinIsOfModel } = useModelsStore()
    return viewerDataTwins.value.filter((v): v is ForgeHubModelTwin =>
      twinIsOfModel<ForgeHubModelTwin>(v, ALLOWED_FORGE_MODEL_IDS)
    )
  })

  /** All Azure Maps viewables in the environment */
  const allMapViewables = computed(() => {
    const twinsStore = useDigitalTwinsStore()
    const { twinIsOfModel } = useModelsStore()
    return twinsStore.allTwins.filter((v): v is MapTwin =>
      twinIsOfModel<MapTwin>(v, ALLOWED_MAP_MODEL_IDS)
    )
  })

  /** Azure Maps viewables (filtered from viewer data twins list) */
  const mapViewables = computed(() => {
    const { twinIsOfModel } = useModelsStore()
    return viewerDataTwins.value.filter((v): v is MapTwin =>
      twinIsOfModel<MapTwin>(v, ALLOWED_MAP_MODEL_IDS)
    )
  })

  /** The viewables for the currently opened viewer */
  const currentViewerViewables = computed((): ViewerDataTwin[] => {
    if (currentViewer.value === ViewerType.ForgeViewer) {
      return forgeViewables.value
    } else if (currentViewer.value === ViewerType.AzureMapsViewer) {
      return mapViewables.value
    } else return []
  })

  /** Return icon based on viewable type */
  function viewerDataIcon(twin?: BasicDigitalTwin, off = false) {
    if (!twin) return undefined
    const { twinIsOfModel } = useModelsStore()
    if (twinIsOfModel(twin, ALLOWED_FORGE_MODEL_IDS))
      return off ? tabOutlineCubeOff : tabOutlineCube
    else if (twinIsOfModel(twin, ALLOWED_MAP_MODEL_IDS))
      return off ? tabOutlineMapMinus : tabOutlineMap
    else return undefined
  }

  /** Return label based on viewable type (each label should also return an icon in viewerDataIcon */
  function viewerDataLabel(twin?: BasicDigitalTwin) {
    const t = i18n.global.t
    if (!twin) return undefined
    const { twinIsOfModel } = useModelsStore()
    if (twinIsOfModel(twin, ALLOWED_FORGE_MODEL_IDS)) return t('viewer.label.model')
    else if (twinIsOfModel(twin, ALLOWED_MAP_MODEL_IDS)) return t('viewer.label.map')
    else return undefined
  }

  /** Whether a marker can be added in the viewer */
  function canAddMarker(twin?: BasicDigitalTwin): boolean {
    const modelsStore = useModelsStore()
    if (currentViewer.value === ViewerType.ForgeViewer && forgeViewables.value.length > 0) {
      if (!twin) return true
      return modelsStore
        .relationships(twin.$metadata.$model)
        .some(
          (r) => r.name === VIEWER_DATA_REL_NAME && r.properties?.some((p) => p.name === 'position')
        )
    } else if (currentViewer.value === ViewerType.AzureMapsViewer) {
      if (!twin) return true
      // TODO: also support geojson geometry
      return !!(
        modelsStore.propertyByName(twin.$metadata.$model, 'latitude') &&
        modelsStore.propertyByName(twin.$metadata.$model, 'longitude')
      )
    } else return false
  }

  /** Whether a marker can be removed in the viewer */
  function canRemoveMarker(twin?: BasicDigitalTwin): boolean {
    const twinsStore = useDigitalTwinsStore()
    if (currentViewer.value === ViewerType.ForgeViewer && forgeViewables.value.length > 0) {
      if (!twin) return true
      return (
        twinsStore.relationships[twin.$dtId]?.[REL_TYPE_OUTGOING]?.some(
          (r) => r.position && currentViewerViewables.value.some((v) => v.$dtId === r.$targetId)
        ) || false
      )
    } else if (currentViewer.value === ViewerType.AzureMapsViewer) {
      if (!twin) return true
      // TODO: also support geojson geometry
      return !!(twin.latitude && twin.longitude)
    } else return false
  }

  /** Whether selected objects from viewer can be imported */
  const canGetObjectPropertiesSelected = computed((): boolean => {
    return currentViewer.value === ViewerType.ForgeViewer && forgeViewables.value.length > 0
  })

  const canGetObjectPropertiesLinked = computed((): boolean => {
    return currentViewer.value === ViewerType.ForgeViewer && forgeViewables.value.length > 0
  })

  /** Whether objects from viewer can be imported */
  const canGetObjectProperties = computed((): boolean => {
    if (currentViewer.value === ViewerType.ForgeViewer && forgeViewables.value.length > 0)
      return true
    else if (currentViewer.value === ViewerType.AzureMapsViewer && mapViewables.value.length > 0)
      return true
    else return false
  })

  /** Whether current viewer state can be saved */
  const canGetState = computed((): boolean => {
    return currentViewer.value === ViewerType.ForgeViewer && forgeViewables.value.length > 0
  })

  /** Whether current selected twin can be linked with the viewer */
  const canAddLink = computed((): boolean => {
    const selectedStore = useSelectedStore()
    const modelsStore = useModelsStore()
    return (
      selectedStore.drawerSelected !== undefined &&
      modelsStore
        .relationships(selectedStore.drawerSelected.$metadata.$model)
        .some(
          (r) =>
            r.name === VIEWER_DATA_REL_NAME && r.properties?.some((p) => p.name === 'externalId')
        )
    )
  })

  /** Whether current selected twin can be unlinked from the viewer */
  const canRemoveLink = computed((): boolean => {
    const selectedStore = useSelectedStore()
    const twinsStore = useDigitalTwinsStore()
    return (
      twinsStore.relationships[selectedStore.drawerSelectedId]?.[REL_TYPE_OUTGOING]?.some(
        (r) => r.externalId && currentViewerViewables.value.some((v) => v.$dtId === r.$targetId)
      ) || false
    )
  })

  function reset() {
    currentViewer.value = ViewerType.AzureMapsViewer
    azureMapsViewer.value = undefined
    forgeViewer.value = undefined
    viewerDataTwins.value = []
    suggestedViewerDataTwin.value = undefined
    twins.value = []
    relationships.value = []
    center.value = undefined
    state.value = {}
    isolate.value = false
    selectedCount.value = 0
  }

  /**
   * Explicitly switch the active viewer without touching loaded viewer data,
   * except: switching to the Forge viewer with no forge model selected yet
   * auto-selects the first available forge model so the 3D viewer is never empty.
   */
  function switchViewer(viewer: ViewerType) {
    if (
      viewer === ViewerType.ForgeViewer &&
      forgeViewables.value.length === 0 &&
      allForgeViewables.value.length > 0
    ) {
      const autoSelected = allForgeViewables.value[0]
      viewerDataTwins.value = [...viewerDataTwins.value, autoSelected]
      // The auto-selected model is loaded in the 3D viewer, so the viewer-data panel must
      // reflect it as visible (the panel rows read visibility from the viewerData store).
      useViewerDataStore().toggleVisibility(autoSelected, true)
    }
    currentViewer.value = viewer
  }

  /**
   * Apply a completed load: replace or extend the loaded set, mark twins no longer loaded as
   * hidden in replace mode, and reflect every loaded twin as visible in the viewerData store.
   */
  function applyLoadedViewerData(
    loaded: BasicDigitalTwin[],
    addMode: boolean,
    visibleAtLoadStart: VisibleSnapshot
  ) {
    const nextTwins = addMode
      ? [
          ...viewerDataTwins.value.filter((x) => !loaded.some((t) => x.$dtId === t.$dtId)),
          ...loaded
        ]
      : [...loaded]
    if (!addMode) {
      // Open (only) replaces the loaded set: every twin no longer on the map must show as off.
      const nextIds = new Set(nextTwins.map((t) => t.$dtId))
      const viewerDataStore = useViewerDataStore()
      for (const twin of viewerDataTwins.value) {
        if (!nextIds.has(twin.$dtId)) viewerDataStore.toggleVisibility(twin, false)
      }
    }
    viewerDataTwins.value = nextTwins
    loaded.forEach((twin) => markViewableVisible(twin as ViewerDataTwin, visibleAtLoadStart))
  }

  /** Loads the viewer data of the provided twin, or tries to retrieve viewer data from the children */
  async function loadViewerData(
    viewables?: BasicDigitalTwin | BasicDigitalTwin[],
    addMode = false
  ) {
    if (!viewables) return
    const _viewables = Array.isArray(viewables) ? viewables : [viewables]
    const { twinIsOfModel } = useModelsStore()
    // Snapshot before the delayed load so a hide that happens in flight can be told apart from
    // a stale hide that predates this load.
    const visibleAtLoadStart = { ...useViewerDataStore().visible }
    await new Promise((resolve) => {
      setTimeout(resolve, 100)
    })
    if (_viewables.every((t) => twinIsOfModel<ForgeHubModelTwin>(t, ALLOWED_FORGE_MODEL_IDS))) {
      // Load Forge Model
      applyLoadedViewerData(_viewables, addMode, visibleAtLoadStart)
      currentViewer.value = ViewerType.ForgeViewer
    } else if (_viewables.every((t) => twinIsOfModel<MapTwin>(t, ALLOWED_MAP_MODEL_IDS))) {
      // Load map viewer data
      applyLoadedViewerData(_viewables, addMode, visibleAtLoadStart)
      currentViewer.value = ViewerType.AzureMapsViewer
    } else {
      // In case the twin itself isn't supported, we try to retrieve supported children and load those
      const { getRelatedTwins } = useDigitalTwinsStore()
      const { twins: mTwins } = await getRelatedTwins(
        _viewables.map((x) => x.$dtId),
        VIEWER_DATA_REL_NAME
      )
      const newForgeViewables = mTwins.filter((v: BasicDigitalTwin): v is ForgeHubModelTwin =>
        twinIsOfModel<ForgeHubModelTwin>(v, ALLOWED_FORGE_MODEL_IDS)
      ) // todo also make sure not to load 2d and 3d at the same time
      const newMapViewables = mTwins.filter((v: BasicDigitalTwin): v is MapTwin =>
        twinIsOfModel<MapTwin>(v, ALLOWED_MAP_MODEL_IDS)
      )
      if (newForgeViewables.length > 0) {
        applyLoadedViewerData(newForgeViewables, addMode, visibleAtLoadStart)
        currentViewer.value = ViewerType.ForgeViewer
      } else if (newMapViewables.length > 0) {
        applyLoadedViewerData(newMapViewables, addMode, visibleAtLoadStart)
        currentViewer.value = ViewerType.AzureMapsViewer
      }
    }
  }

  /** Remove viewer data by providing the twin id */
  function removeViewerData(twin: BasicDigitalTwin) {
    const idx = viewerDataTwins.value.findIndex((x) => x.$dtId === twin.$dtId)
    if (idx > -1) {
      viewerDataTwins.value.splice(idx, 1)
      // An unloaded layer is no longer on the map, so the panel must show it as off.
      useViewerDataStore().toggleVisibility(twin as ViewerDataTwin, false)
      const { twinIsOfModel } = useModelsStore()
      // For Forge viewer (or other viewer that needs to have something loaded, make sure to reset the current viewer to the default)
      if (twinIsOfModel(twin, ALLOWED_FORGE_MODEL_IDS) && !forgeViewables.value.length)
        currentViewer.value = ViewerType.AzureMapsViewer
    }
  }

  /** Save marker position for the provided twin. Actions depend on current viewer */
  async function addMarker(twin: BasicDigitalTwin) {
    if (!canAddMarker(twin)) return
    if (
      !currentViewerComponent.value ||
      typeof currentViewerComponent.value.addMarker !== 'function'
    )
      return

    // TODO: set loading (disable buttons in viewer) and allow to cancel (add a cancel button in viewerscontainer)

    const marker = await (currentViewerComponent.value.addMarker as AddMarkerFunction)()
    if (!marker) return

    const twinsStore = useDigitalTwinsStore()
    // The returned marker format depends on the viewer
    if (
      marker.viewableTwinId &&
      !Array.isArray(marker.position) &&
      marker.position.x !== undefined &&
      marker.position.y !== undefined &&
      marker.position.z !== undefined
    ) {
      // store in relationship for forge viewer
      return twinsStore.addRelationship({
        $relationshipId: uid(),
        $relationshipName: VIEWER_DATA_REL_NAME,
        $sourceId: twin.$dtId,
        $targetId: marker.viewableTwinId,
        position: marker.position
      })
    } else if (
      currentViewer.value === ViewerType.AzureMapsViewer &&
      Array.isArray(marker.position)
    ) {
      // store in latitude and longitude properties for azure maps viewer
      return twinsStore.updateTwin(twin.$dtId, [
        {
          op: 'add',
          path: '/longitude',
          value: marker.position[0]
        },
        {
          op: 'add',
          path: '/latitude',
          value: marker.position[1]
        }
      ])
    }
  }

  async function getSelectedViewerObject(twinId: string) {
    // Check if viewer component is defined and whether it has a an exposed method
    if (
      !currentViewerComponent.value ||
      typeof currentViewerComponent.value.getObjectProperties !== 'function'
    )
      return
    // '='-prefixed twinIdProperty: matched by evaluating the expression per object
    const expressionTwinIdProperties = collectExpressionTwinIdProperties(viewerDataTwins.value)
    // Legacy name-based twinIdProperty: plain name or JEXL name list (excludes expressions)
    const twinIdProperties = collectNameTwinIdProperties(viewerDataTwins.value)
    const { objects } = await (
      currentViewerComponent.value.getObjectProperties as GetObjectPropertiesFunction
    )('selected')
    return (
      findByNameTwinId(objects, twinIdProperties, twinId) ??
      findByExpressionTwinId(objects, expressionTwinIdProperties, twinId)
    )
  }

  async function linkTwin(twinId: string) {
    // Check if viewer component is defined and whether it has a an exposed method
    if (
      !currentViewerComponent.value ||
      typeof currentViewerComponent.value.getObjectProperties !== 'function'
    )
      return
    const twinsStore = useDigitalTwinsStore()
    const { objects } = await (
      currentViewerComponent.value.getObjectProperties as GetObjectPropertiesFunction
    )('selected')
    const newRels = objects.map((obj) => ({
      $relationshipId: uid(),
      $relationshipName: VIEWER_DATA_REL_NAME,
      $sourceId: twinId,
      $targetId: obj.viewableTwinId,
      externalId: obj.externalId,
      ...(obj.layerId && { layerId: obj.layerId })
    }))
    // TODO: filter out existing to avoid duplicates
    return twinsStore.addRelationships(newRels)
    // TODO: notify success
  }

  /** Removes all links (and markers in case stored in the relationship) */
  async function removeTwinLinks(twinId: string) {
    const twinsStore = useDigitalTwinsStore()
    const rels = twinsStore.relationships[twinId]?.[REL_TYPE_OUTGOING]?.filter((r) =>
      currentViewerViewables.value.some((v) => v.$dtId === r.$targetId)
    )
    if (!rels || rels.length === 0) return
    return twinsStore.deleteRelationships(rels)
    // TODO: notify success
  }

  function restoreViewerStateFromRelation(twinId: string) {
    const twinsStore = useDigitalTwinsStore()
    // Find relationship and get viewer state
    const viewerState = twinsStore.relationships[twinId]?.[REL_TYPE_OUTGOING]?.find(
      (r) =>
        r.$relationshipName === VIEWER_DATA_REL_NAME &&
        r.viewerState &&
        currentViewerViewables.value.find((v) => v.$dtId === r.$targetId)
    )?.viewerState as string
    // Check if viewer component is defined and whether it has a an exposed method
    if (!viewerState) return
    // if (!viewerState || !viewersStore.currentViewerComponent || typeof viewersStore.currentViewerComponent.restoreState !== 'function') return
    // Restore state in viewer
    // viewersStore.currentViewerComponent.restoreState(viewerState)
    // viewer state in store is a prop to the viewer
    state.value = JSON.parse(viewerState)
  }

  function saveViewerStateInRelation(twinId: string) {
    const twinsStore = useDigitalTwinsStore()
    // Check if viewer component is defined and whether it has a an exposed method
    if (
      !currentViewerComponent.value ||
      /* !(this.currentViewerComponent instanceof ForgeViewer) ||  */ typeof (
        currentViewerComponent.value as InstanceType<typeof ForgeViewer>
      ).getState !== 'function'
    )
      return
    const viewerState = (
      currentViewerComponent.value as InstanceType<typeof ForgeViewer>
    ).getState()
    if (!viewerState) return
    // find existing relationship
    const existingRel = twinsStore.relationships[twinId]?.[REL_TYPE_OUTGOING]?.find(
      (r) =>
        r.$relationshipName === VIEWER_DATA_REL_NAME &&
        currentViewerViewables.value.find(
          (v) => v.$dtId === r.$targetId && v.urn === viewerState.seedURN
        )
    )
    if (existingRel) {
      twinsStore.updateRelationship(existingRel.$sourceId, existingRel.$relationshipId, [
        {
          op: 'add',
          path: '/viewerState',
          value: JSON.stringify(viewerState)
        }
      ])
    } else {
      const targetViewableId = currentViewerViewables.value.find(
        (v) => v.urn === viewerState.seedURN
      )?.$dtId
      if (!targetViewableId) return
      twinsStore.addRelationship({
        $relationshipId: uid(),
        $sourceId: twinId,
        $relationshipName: VIEWER_DATA_REL_NAME,
        $targetId: targetViewableId,
        viewerState: JSON.stringify(viewerState)
      })
    }
    // TODO: notify that state was saved succesfully
  }

  return {
    currentViewer,
    azureMapsViewer,
    forgeViewer,
    viewerDataTwins,
    suggestedViewerDataTwin,
    twins,
    relationships,
    center,
    state,
    isolate,
    selectedCount,
    currentViewerComponent,
    allForgeViewables,
    forgeViewables,
    allMapViewables,
    mapViewables,
    currentViewerViewables,
    viewerDataIcon,
    viewerDataLabel,
    canAddMarker,
    canRemoveMarker,
    canGetObjectPropertiesSelected,
    canGetObjectPropertiesLinked,
    canGetObjectProperties,
    canGetState,
    canAddLink,
    canRemoveLink,
    reset,
    switchViewer,
    loadViewerData,
    removeViewerData,
    addMarker,
    getSelectedViewerObject,
    linkTwin,
    removeTwinLinks,
    restoreViewerStateFromRelation,
    saveViewerStateInRelation
  }
})
